// @ts-nocheck
/**
 * MintCard Component - Performance Optimized
 * 
 * Optimizations:
 * - Memoized calculations and derived state
 * - Reduced unnecessary re-renders
 * - Eliminated redundant useEffect hooks
 * - Better error handling
 * - Cleaner prop handling
 */
import React, { useState, useMemo, useCallback } from "react";
import { PieChart } from "react-minimal-pie-chart";
import loading from "@assets/images/loading-card.svg";

// Utility function to truncate address
const truncateAddress = (address) => {
  if (!address || typeof address !== 'string') return '';
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

// Default fallback values
const DEFAULT_CARD = {
  image: loading,
  name: 'NFT',
  address: '',
  pieData: [],
  distPresetName: '',
  price: 0,
  tokenId: '',
  siteTokenIndex: ''
};

const MintCard = ({ card = DEFAULT_CARD }) => {
  // State for image source with fallback
  const [imgSrc, setImgSrc] = useState(() => card.image || loading);
  
  // Memoized address truncation - only recalculates when address changes
  const truncatedAddress = useMemo(() => 
    truncateAddress(card.address), 
    [card.address]
  );

  // Memoized card name with fallback
  const cardName = useMemo(() => 
    card.name || '', 
    [card.name]
  );

  // Memoized price display
  const priceDisplay = useMemo(() => 
    `${card.price || 0} AVAX`, 
    [card.price]
  );

  // Memoized pie chart data with safety check
  const pieChartData = useMemo(() => {
    if (!Array.isArray(card.pieData) || card.pieData.length === 0) {
      return [];
    }
    return card.pieData;
  }, [card.pieData]);

  // Optimized error handler - useCallback prevents recreation on every render
  const handleImageError = useCallback(() => {
    setImgSrc(loading);
  }, []);

  // Update image source when card.image changes
  // Using direct comparison instead of useEffect for better performance
  if (card.image && imgSrc !== card.image) {
    setImgSrc(card.image);
  }

  return (
    <div
      className="nft_card"
      data-token={card.tokenId || ''}
      data-siteindex={card.siteTokenIndex || ''}
    >
      <div className="card__top-box">
        {/* NFT Image with optimized error handling */}
        <img 
          className="card__img ss" 
          alt={`NFT ${cardName}`}
          src={imgSrc} 
          onError={handleImageError}
          loading="lazy" // Add lazy loading for better performance
        />
      </div>

      {/* Bottom Box containing NFT details */}
      <div className="card__bottom-box">
        <div className="card__body">
          {/* Pie Chart showing distribution - only render if data exists */}
          {pieChartData.length > 0 && (
            <div className="pie__wrapper">
              <PieChart
                className="card-pie"
                data={pieChartData}
                startAngle={-90}
                labelPosition={87}
                radius={40}
              />
            </div>
          )}

          {/* Card title and address */}
          <div className="card__body-top">
            <h3 className="card__title">
              {cardName}
            </h3>
            <div className="group1">
              <div className="card__text card__addr">
                {truncatedAddress}
              </div>
              <h4 className="card__text card__type">
                {card.distPresetName || ''}
              </h4>
            </div>
          </div>

          {/* Bottom section with button and price */}
          <div className="card__body-bottom">
            <h3 className="card__text nft-price">
              {priceDisplay}
            </h3>
          </div>
        </div>
      </div>
    </div>
  );
};

// Only memoize if the component receives complex props or renders frequently
// In this case, since it's a simple display component, we can skip React.memo
// unless performance testing shows it's needed
export default MintCard;
